import type { AnswerPayload, ChatEffect, ChatEvent, ChatMessage, ChatScript, ChatStep, Prompt } from './types';
import type { QuestionType } from '@/lib/analytics';
import type { RichTextField } from '@prismicio/client';
import { mistakeFeedback } from './activity';
import { discoveryProgress } from './discovery';
import { labelText } from './label';
import { calculateStarRating } from './scoring';

/**
 * The chat engine. A pure reducer: no timers, no Prismic reads, no store
 * writes. `pacing.ts` decides *when* to dispatch `advance`, and `store.ts`
 * drains `effects` into the user store and analytics — which means every rule
 * in here (what a slice becomes, when a quiz retries, when a slice counts)
 * is testable by dispatching events at a fixture.
 */

export type ChatStatus = 'idle' | 'playing' | 'awaiting' | 'complete';

export type ChatState = {
  status: ChatStatus;
  script: ChatScript;
  /** Index of the step being played. Equals `steps.length` once they run out. */
  cursor: number;
  /** Messages that have landed, oldest first. */
  thread: Array<ChatMessage>;
  /** Messages waiting for the pacing layer to land them, in order. */
  queue: Array<ChatMessage>;
  /** What the answer control is showing. Only ever set while `awaiting`. */
  pending: Prompt | null;
  /** Held back until the queue drains, so the question lands before its options. */
  staged: Prompt | null;
  /** Attempts on the current prompt, 1-based. */
  attempt: number;
  /** The run's counters, mirrored into the user store by the effects. */
  counters: { completed: number; tried: number; answered: number };
  /** Drained by the caller after every dispatch. */
  effects: Array<ChatEffect>;
};

const emptyScript: ChatScript = {
  meta: { moduleKey: '', moduleIndex: 1, moduleTotal: 1, title: '', upNext: null },
  steps: [],
  checkpoints: [],
};

export const initialChatState: ChatState = {
  status: 'idle',
  script: emptyScript,
  cursor: 0,
  thread: [],
  queue: [],
  pending: null,
  staged: null,
  attempt: 1,
  counters: { completed: 0, tried: 0, answered: 0 },
  effects: [],
};

// --- Selectors ---------------------------------------------------------------

/**
 * The checkpoint the learner is inside, 1-based. Zero dividers landed still
 * reads as the first checkpoint: the learner is in it, it just has no marker
 * above it yet.
 */
export function currentCheckpoint(state: ChatState): number {
  const landed = state.thread.filter((message) => message.kind === 'marker').length;

  return Math.max(1, landed);
}

/** How far through a module the learner is, counted in script steps. */
export type ModuleProgress = {
  stepsDone: number;
  stepsTotal: number;
  /** 0–1, and exactly 1 once the module is complete. */
  fraction: number;
};

/**
 * Which checkpoint owns each step, 1-based and in step order.
 *
 * Steps do not carry a checkpoint index of their own — only a `divider` step
 * knows one — so ownership is the running divider. Both things that open a
 * checkpoint emit a divider step (`section_divider`, and the scrollytelling
 * bridge), which is what makes the walk complete rather than a guess.
 */
function checkpointOwners(script: ChatScript): Array<number> {
  let current = script.checkpoints[0]?.index ?? 1;

  return script.steps.map((step) => {
    if (step.kind === 'divider') {
      current = step.index;
    }

    return current;
  });
}

/** Steps consumed so far, saturating at the script's length. */
function stepsConsumed(script: ChatScript, state: ChatState): number {
  return state.status === 'complete' ? script.steps.length : Math.min(state.cursor, script.steps.length);
}

/**
 * How far into the module the learner has played, as a fraction of its steps.
 *
 * Counted from the cursor rather than from landed messages, so it measures
 * script consumed and not pacing: a resumed run replays with pacing disabled
 * and still reports the place it resumed to.
 *
 * Reads the `script` argument for the same reason `checkpointStates` does —
 * the reducer is `idle`, with an empty script, until `start` fires in a client
 * effect.
 */
export function moduleProgress(script: ChatScript, state: ChatState): ModuleProgress {
  const stepsTotal = script.steps.length;
  const stepsDone = stepsConsumed(script, state);

  return {
    stepsDone,
    stepsTotal,
    fraction: stepsTotal === 0 ? (state.status === 'complete' ? 1 : 0) : stepsDone / stepsTotal,
  };
}

/**
 * The header's segments, with the state each one draws in and how full it is.
 *
 * `fill` is what makes the rail a progress indicator rather than a set of
 * lamps: the segment the learner is inside fills with the steps they have
 * played, so a module with no `section_divider` — one single segment — reads as
 * real progress instead of sitting solid from the first message.
 *
 * The checkpoint list comes from the `script` argument rather than
 * `state.script`, because the reducer is `idle` — and its script empty — until
 * `start` fires in a client effect. Reading the run would render a header with
 * no segments on the server and then grow it by a row on hydration.
 */
export function checkpointStates(script: ChatScript, state: ChatState) {
  const current = currentCheckpoint(state);
  const finished = state.status === 'complete';
  const owners = checkpointOwners(script);
  const consumed = stepsConsumed(script, state);

  return script.checkpoints.map((checkpoint) => {
    const state_ =
      finished || checkpoint.index < current
        ? ('done' as const)
        : checkpoint.index === current
          ? ('current' as const)
          : ('todo' as const);

    const owned = owners.reduce((count, owner) => (owner === checkpoint.index ? count + 1 : count), 0);
    const played = owners.reduce(
      (count, owner, at) => (owner === checkpoint.index && at < consumed ? count + 1 : count),
      0,
    );

    return {
      ...checkpoint,
      state: state_,
      // A segment behind the learner is full whether or not its steps were
      // counted — `complete` skips straight to the end of the script.
      fill: state_ === 'done' ? 1 : owned === 0 ? 0 : played / owned,
    };
  });
}

/** The star rating this run has earned so far — `calculateStarRating` on wrong attempts. */
export function runScore(state: ChatState): number {
  return calculateStarRating(state.counters.tried - state.counters.answered);
}

/** True while the module still has slices to play. Drives `topic_abandon`. */
export function isMidModule(state: ChatState): boolean {
  return state.status !== 'idle' && state.status !== 'complete';
}

// --- Step → messages ---------------------------------------------------------

/** The analytics `question_type` for a step that can be answered. */
function questionTypeOf(step: ChatStep): QuestionType {
  switch (step.kind) {
    case 'quiz':
      return 'quiz';
    case 'checklist':
      return 'text_checklist';
    case 'interaction':
      return 'user_interaction';
    case 'discovery':
      return 'discovery';
    default:
      return 'activity';
  }
}

/**
 * What one step contributes: the messages it lands, and the prompt (if any)
 * that arms once they have. The prompt's `questionIndex` is its position
 * among the module's answerable steps, which is what GA4 reports.
 */
function playStep(step: ChatStep, questionIndex: number): { messages: Array<ChatMessage>; prompt: Prompt | null } {
  switch (step.kind) {
    case 'coach':
      return { messages: [{ id: step.id, kind: 'coach', text: step.text }], prompt: null };

    case 'image':
      return { messages: [{ id: step.id, kind: 'image', image: step.image }], prompt: null };

    case 'carousel':
      return { messages: [{ id: step.id, kind: 'carousel', images: step.images }], prompt: null };

    case 'video':
      return {
        messages: [
          {
            id: step.id,
            kind: 'video',
            title: step.title,
            subtitle: step.subtitle,
            poster: step.poster,
            embedLink: step.embedLink,
            transcript: step.transcript,
          },
        ],
        // A video is a gate: only Continue in the player dialog moves on.
        prompt: { kind: 'gate', stepId: step.id },
      };

    case 'divider':
      return { messages: [{ id: step.id, kind: 'marker', index: step.index, title: step.title }], prompt: null };

    case 'takeaways':
      return {
        messages: [{ id: step.id, kind: 'takeaways', heading: step.heading, points: step.points }],
        prompt: null,
      };

    case 'quiz':
      return {
        messages: [{ id: `${step.id}-q`, kind: 'coach', text: step.question, prompt: true }],
        prompt: {
          kind: 'single',
          stepId: step.id,
          questionId: step.questionId,
          questionIndex,
          options: step.answers.map((answer, index) => ({
            id: `${step.id}-a${index}`,
            label: answer.label,
            // `accepted_answer` is 1-based, exactly as authored.
            correct: step.acceptedAnswer === index + 1,
          })),
        },
      };

    case 'checklist':
      return {
        messages: [{ id: `${step.id}-q`, kind: 'coach', text: step.question, prompt: true }],
        prompt: {
          kind: 'multi',
          stepId: step.id,
          questionId: step.questionId,
          questionIndex,
          min: 1,
          options: step.items.map((item, index) => ({
            id: `${step.id}-i${index}`,
            label: item.label,
            correct: item.correct,
          })),
        },
      };

    case 'interaction':
      return {
        messages: [{ id: `${step.id}-q`, kind: 'coach', text: step.question ?? '', prompt: true }],
        prompt: {
          kind: 'quick',
          stepId: step.id,
          questionId: step.questionId,
          questionIndex,
          options: step.options.map((option, index) => ({
            id: `${step.id}-o${index}`,
            label: option.label,
            // Acknowledgements have no wrong answer.
            correct: true,
          })),
        },
      };

    case 'discovery':
      return {
        // The intro is authored on the slice, so a set says what it is without
        // needing a `content_bubble` in front of it.
        messages: step.intro.length > 0 ? [{ id: `${step.id}-intro`, kind: 'coach', text: step.intro }] : [],
        // An unauthored set has nothing to pick: it lands its intro and moves
        // on rather than arming a prompt with no options.
        prompt:
          step.entries.length === 0
            ? null
            : {
                kind: 'discover',
                stepId: step.id,
                questionId: step.questionId,
                questionIndex,
                heading: step.prompt,
                options: step.entries.map((entry) => ({ id: entry.id, label: entry.name, correct: true })),
                done: 0,
                total: step.entries.length,
              },
      };

    case 'activity':
      return {
        messages: [
          // The intro is authored on the slice, so the coach still says what
          // the card is for without a `content_bubble` in front of every one.
          ...(step.slice.primary.intro.length > 0
            ? [{ id: `${step.id}-intro`, kind: 'coach' as const, text: step.slice.primary.intro }]
            : []),
          { id: step.id, kind: 'activity', slice: step.slice, answered: false, revealed: false },
        ],
        prompt:
          step.slice.variation === 'free_text'
            ? {
                kind: 'text',
                stepId: step.id,
                questionId: step.questionId,
                questionIndex,
                placeholder: step.slice.primary.placeholder,
                maxLength: step.slice.primary.max_length ?? 140,
              }
            : { kind: 'activity', stepId: step.id, questionId: step.questionId, questionIndex, slice: step.slice },
      };
  }
}

/**
 * Wrong checks before an activity offers more help. A wrong check never ends
 * the activity. After `ACTIVITY_HINT_AFTER` the card shows its hint, after
 * `ACTIVITY_AREA_HINT_AFTER` it also marks a search area or an ordering's first
 * step, and after `ACTIVITY_REVEAL_AFTER` the footer offers "Show me the
 * answer", the `reveal` event.
 */
export const ACTIVITY_HINT_AFTER = 1;
export const ACTIVITY_AREA_HINT_AFTER = 2;
export const ACTIVITY_REVEAL_AFTER = 3;

/** True on an attempt that comes after `wrongs` wrong checks. */
export const pastWrongs = (attempt: number, wrongs: number) => attempt > wrongs;

/** Steps the learner answers, in order — the source of `question_index`. */
const ANSWERABLE = new Set<ChatStep['kind']>(['quiz', 'checklist', 'interaction', 'discovery', 'activity']);

function questionIndexOf(script: ChatScript, cursor: number): number {
  let index = 0;

  for (let i = 0; i <= cursor && i < script.steps.length; i += 1) {
    const step = script.steps[i];

    if (step && ANSWERABLE.has(step.kind)) {
      index += 1;
    }
  }

  return index;
}

// --- Transitions -------------------------------------------------------------

/** Queues the step at `cursor`, or the completion card when they run out. */
function enqueueCurrentStep(state: ChatState): ChatState {
  const step = state.script.steps[state.cursor];

  if (!step) {
    return finish(state);
  }

  const { messages, prompt } = playStep(step, questionIndexOf(state.script, state.cursor));

  return { ...state, status: 'playing', queue: messages, staged: prompt, pending: null, attempt: 1 };
}

/** Queues the completion card. Landing it (in `advance`) turns the run `complete`. */
function finish(state: ChatState): ChatState {
  const { meta } = state.script;

  return {
    ...state,
    status: 'playing',
    queue: [
      {
        id: 'completion',
        kind: 'completion',
        score: runScore(state),
        moduleIndex: meta.moduleIndex,
        moduleTotal: meta.moduleTotal,
        upNext: meta.upNext,
      },
    ],
    staged: null,
    pending: null,
  };
}

/**
 * One pacing tick. In order of priority: finish the current step and queue the
 * next, land a queued message, arm a staged prompt. The completion card is the
 * last thing to land, and landing it ends the run.
 */
function advance(state: ChatState): ChatState {
  let next = state;

  if (next.queue.length === 0 && next.pending === null && next.staged === null) {
    if (next.status === 'complete') {
      return next;
    }

    if (next.cursor >= next.script.steps.length) {
      // The completion card has landed and there is nothing left to play.
      return {
        ...next,
        status: 'complete',
        effects: [...next.effects, { kind: 'module_completed', score: runScore(next) }],
      };
    }

    const step = next.script.steps[next.cursor];

    if (!step) {
      return next;
    }

    next = {
      ...next,
      cursor: next.cursor + 1,
      counters: { ...next.counters, completed: next.counters.completed + 1 },
      effects: [...next.effects, { kind: 'slice_completed', stepId: step.id }],
    };

    next = enqueueCurrentStep(next);
  }

  const [message, ...rest] = next.queue;

  if (message) {
    next = { ...next, thread: [...next.thread, message], queue: rest };

    // The completion card landing *is* the end of the run: score it now, in the
    // same tick, rather than on the next pacing tick. A learner who taps
    // Continue the moment the card appears — or Autopilot at Instant speed —
    // otherwise leaves the module with every slice complete and no score.
    if (message.kind === 'completion' && next.status !== 'complete') {
      next = {
        ...next,
        status: 'complete',
        effects: [...next.effects, { kind: 'module_completed', score: runScore(next) }],
      };
    }
  }

  if (next.queue.length === 0 && next.staged !== null) {
    next = { ...next, status: 'awaiting', pending: next.staged, staged: null };
  }

  return next;
}

/**
 * The label echoed back as the learner's own bubble, and — when the option has
 * no words at all — its 1-based position, so the renderer can name it from the
 * catalogue rather than landing an empty bubble.
 */
function echoLabel(prompt: Prompt, payload: AnswerPayload): { text: string; optionNumber?: number } | null {
  const options = 'options' in prompt ? prompt.options : [];
  const optionText = (id: string) => {
    const option = options.find((candidate) => candidate.id === id);

    // A picture answer echoes its alt text, which is the only words it has.
    return option ? labelText(option.label) : '';
  };
  const positionOf = (id: string) => options.findIndex((candidate) => candidate.id === id) + 1;

  switch (payload.kind) {
    case 'quick':
    case 'single':
    case 'discover': {
      const text = optionText(payload.optionId);
      const position = positionOf(payload.optionId);

      return text === '' && position > 0 ? { text, optionNumber: position } : { text };
    }
    case 'multi':
      return { text: payload.optionIds.map(optionText).filter(Boolean).join(', ') };
    case 'text':
      return { text: payload.value };
    case 'activity':
      return { text: payload.receipt };
    case 'gate':
      return null;
  }
}

/** The verdict on one answer, or `null` for a gate, which is not an answer at all. */
type Verdict = { correct: boolean; scores: boolean };

/** Marks an answer. `scores` mirrors the current app: acknowledgements are never scored. */
function mark(prompt: Prompt, payload: AnswerPayload): Verdict | null {
  switch (payload.kind) {
    case 'single': {
      const option =
        'options' in prompt ? prompt.options.find((candidate) => candidate.id === payload.optionId) : undefined;

      return { correct: option?.correct === true, scores: true };
    }

    case 'multi': {
      if (!('options' in prompt)) {
        return { correct: false, scores: true };
      }

      const expected = prompt.options.filter((option) => option.correct);
      const picked = new Set(payload.optionIds);
      // Exact set: every correct item ticked and nothing else.
      const correct = picked.size === expected.length && expected.every((option) => picked.has(option.id));

      return { correct, scores: true };
    }

    // Acknowledgements, discoveries, free text and gates: reported to GA4,
    // never scored. None of them has a wrong answer to get wrong.
    case 'quick':
    case 'discover':
    case 'text':
      return { correct: true, scores: false };

    case 'activity':
      return { correct: payload.correct, scores: true };

    case 'gate':
      return null;
  }
}

/** The authored feedback for the answer just given, or `null` for the fallback copy. */
function feedbackFor(
  step: ChatStep,
  payload: AnswerPayload,
  attempt: number,
  verdict: Verdict | null,
): ChatMessage | null {
  // Keyed by attempt, so the same wrong answer twice is two bubbles.
  const id = `${step.id}-fb-${attempt}-`;

  if (step.kind === 'quiz' && payload.kind === 'single') {
    const index = step.answers.findIndex((_answer, position) => `${step.id}-a${position}` === payload.optionId);
    const answer = step.answers[index];
    const correct = step.acceptedAnswer === index + 1;

    if (answer && answer.feedback.length > 0) {
      return { id: `${id}${index}`, kind: 'feedback', correct, text: answer.feedback };
    }

    // The current app shows nothing when a right answer has no feedback, but a
    // wrong one always needs to say so or the retry looks like a dead tap.
    return correct ? null : { id: `${id}wrong`, kind: 'feedback', correct: false, text: null };
  }

  // A right set gets `response_text` when the step moves on. A wrong one gets
  // the feedback on every item it got wrong, in one bubble, or the slice's own
  // wrong line when those items carry none.
  if (step.kind === 'checklist' && payload.kind === 'multi') {
    if (verdict?.correct !== false) {
      return null;
    }

    const picked = new Set(payload.optionIds);
    const [first, ...rest] = step.items
      .filter((item, index) => picked.has(`${step.id}-i${index}`) !== item.correct)
      .flatMap((item) => item.feedback);
    const text: RichTextField = first ? [first, ...rest] : step.feedbackWrong;

    return text.length > 0 ? { id: `${id}wrong`, kind: 'feedback', correct: false, text } : null;
  }

  if (step.kind === 'activity' && (payload.kind === 'activity' || payload.kind === 'text')) {
    const correct = payload.kind === 'text' || payload.correct;

    // Feedback on the item the learner got wrong explains this mistake, so it
    // is said every time.
    const specific = !correct && payload.kind === 'activity' ? mistakeFeedback(step.slice, payload.mistake) : null;

    if (specific) {
      return { id: `${id}wrong`, kind: 'feedback', correct: false, text: specific };
    }

    // The general explanation is said once. Repeats would push the card, and
    // its hint, up the thread, so later misses get a short bubble instead.
    if (!correct && pastWrongs(attempt, ACTIVITY_HINT_AFTER)) {
      return { id: `${id}wrong`, kind: 'feedback', correct: false, text: null, hint: true };
    }

    const authored = correct ? step.slice.primary.feedback_correct : step.slice.primary.feedback_wrong;

    return authored.length > 0
      ? { id: `${id}${correct ? 'right' : 'wrong'}`, kind: 'feedback', correct, text: authored }
      : { id: `${id}${correct ? 'right' : 'wrong'}`, kind: 'feedback', correct, text: null };
  }

  if (step.kind === 'interaction' && payload.kind === 'quick') {
    const index = step.options.findIndex((_option, position) => `${step.id}-o${position}` === payload.optionId);
    const option = step.options[index];

    // An acknowledgement has no right answer, so its reply is the coach
    // carrying on — not feedback. A `feedback` message would draw the green
    // "correct" rail against "Great, let's get into it".
    return option && option.feedback.length > 0 ? { id: `${id}${index}`, kind: 'coach', text: option.feedback } : null;
  }

  return null;
}

/**
 * One pick in a discovery: land what the entry has to show, take it off the
 * list, and hand back the rest.
 *
 * This is the only prompt that stages *itself* again. Everything else leaves a
 * step the moment it is answered; a discovery is finished by the learner
 * exhausting it, so the step advances only once the list is empty — which is
 * what makes the set one slice on the module's rail however many entries it
 * holds.
 */
function discover(
  state: ChatState,
  step: ChatStep & { kind: 'discovery' },
  prompt: Prompt & { kind: 'discover' },
  optionId: string,
  carried: { thread: Array<ChatMessage>; counters: ChatState['counters']; effects: Array<ChatEffect> },
): ChatState {
  const entry = step.entries.find((candidate) => candidate.id === optionId);

  if (!entry) {
    return state;
  }

  const remaining = prompt.options.filter((option) => option.id !== optionId);
  const total = step.entries.length;
  const done = total - remaining.length;

  const queue: Array<ChatMessage> = [
    { id: `${entry.id}-image`, kind: 'image', image: entry.image },
    ...entry.details.map((text, index) => ({ id: `${entry.id}-d${index}`, kind: 'coach' as const, text })),
  ];

  const progress = discoveryProgress(step.progressLabel, done, total);

  if (progress !== null) {
    queue.push({ id: `${entry.id}-progress`, kind: 'discovered', text: progress, done, total });
  }

  const next = { ...state, status: 'playing' as const, queue, pending: null, ...carried };

  if (remaining.length === 0) {
    if (step.outro.length > 0) {
      queue.push({ id: `${step.id}-outro`, kind: 'coach', text: step.outro });
    }

    return { ...next, staged: null };
  }

  return {
    ...next,
    staged: {
      ...prompt,
      // The second instruction is optional; without one the same line stands.
      heading: prompt.done === 0 && step.promptAgain?.trim() ? step.promptAgain : prompt.heading,
      options: remaining,
      done,
    },
    // The pick's position in the set, which is what its GA4 `attempt` reports.
    attempt: state.attempt + 1,
  };
}

function answer(state: ChatState, payload: AnswerPayload): ChatState {
  const { pending } = state;

  if (!pending || pending.kind !== payload.kind || state.status !== 'awaiting') {
    return state;
  }

  const step = state.script.steps.find((candidate) => candidate.id === pending.stepId);

  if (!step) {
    return state;
  }

  const verdict = mark(pending, payload);
  const effects = [...state.effects];
  const queue: Array<ChatMessage> = [];
  const thread = [...state.thread];
  const counters = { ...state.counters };

  const echo = echoLabel(pending, payload);

  if (echo !== null && (echo.text !== '' || echo.optionNumber !== undefined)) {
    // The learner's own words land immediately: the chip they tapped has
    // already animated away, so a delay here reads as a dropped tap.
    thread.push({ id: `${step.id}-echo-${state.attempt}`, kind: 'user', ...echo });
  }

  // A one-option quick reply ("Yes, I'm ready!") is the learner nodding along:
  // there is no choice in it, so it is not reported as an answer at all. A
  // quick reply with a real choice still is, like every scored question.
  const acknowledgement = pending.kind === 'quick' && pending.options.length < 2;

  if (verdict && !acknowledgement) {
    if (verdict.scores) {
      counters.tried += 1;
      counters.answered += verdict.correct ? 1 : 0;
    }

    effects.push({
      kind: 'attempt',
      questionId: 'questionId' in pending ? pending.questionId : step.id,
      questionIndex: 'questionIndex' in pending ? pending.questionIndex : 0,
      questionType: questionTypeOf(step),
      correct: verdict.correct,
      attempt: state.attempt,
      scores: verdict.scores,
    });
  }

  if (step.kind === 'discovery' && payload.kind === 'discover' && pending.kind === 'discover') {
    return discover(state, step, pending, payload.optionId, { thread, counters, effects });
  }

  const authored = feedbackFor(step, payload, state.attempt, verdict);
  const wrong = verdict !== null && !verdict.correct;

  if (authored) {
    queue.push(authored);
  }

  // Wrong answers retry until correct, activities included. An activity's way
  // out is the `reveal` event.
  if (wrong) {
    if (!authored) {
      queue.push({ id: `${step.id}-fb-${state.attempt}`, kind: 'feedback', correct: false, text: null });
    }

    return {
      ...state,
      status: 'playing',
      thread,
      queue,
      pending: null,
      staged: pending,
      attempt: state.attempt + 1,
      counters,
      effects,
    };
  }

  // Right, or nothing to mark: play any follow-up copy and move on.
  if (step.kind === 'checklist') {
    if (step.responseText.length > 0) {
      queue.push({ id: `${step.id}-response`, kind: 'coach', text: step.responseText });
    } else {
      queue.push({ id: `${step.id}-response`, kind: 'feedback', correct: true, text: null });
    }
  }

  if (step.kind === 'activity') {
    // Freeze the card as a record. Only `reveal` draws the answer on it.
    freezeActivity(thread, step, false);
  }

  return { ...state, status: 'playing', thread, queue, pending: null, staged: null, counters, effects };
}

/**
 * Replaces the activity card in the thread with its frozen state, in place.
 * `revealed` draws the answer on it — the reveal image, or the correct order.
 */
function freezeActivity(thread: Array<ChatMessage>, step: ChatStep & { kind: 'activity' }, revealed: boolean) {
  const index = thread.findIndex((message) => message.id === step.id);

  if (index >= 0) {
    thread[index] = { id: step.id, kind: 'activity', slice: step.slice, answered: true, revealed };
  }
}

/** Re-arms the prompt after wrong-answer feedback. Normally the queue does this. */
function retry(state: ChatState): ChatState {
  return state.staged === null ? state : { ...state, status: 'awaiting', pending: state.staged, staged: null };
}

/**
 * Moves on without marking the current prompt correct, when the learner asks
 * to see the answer. An activity card freezes with the answer drawn on it.
 */
function reveal(state: ChatState): ChatState {
  const prompt = state.pending ?? state.staged;

  if (prompt === null) {
    return state;
  }

  const step = state.script.steps.find((candidate) => candidate.id === prompt.stepId);
  const thread = [...state.thread];
  const queue: Array<ChatMessage> = [];

  if (step?.kind === 'activity') {
    freezeActivity(thread, step, true);
    queue.push({ id: `${step.id}-fb-reveal`, kind: 'feedback', correct: false, text: null, reveal: true });
  }

  return { ...state, status: 'playing', thread, queue, pending: null, staged: null };
}

export function chatReducer(state: ChatState, event: ChatEvent): ChatState {
  switch (event.type) {
    case 'start':
      return enqueueCurrentStep({
        ...initialChatState,
        script: event.script,
      });

    case 'advance':
      return advance(state);

    case 'answer':
      return answer(state, event.payload);

    case 'retry':
      return retry(state);

    case 'reveal':
      return reveal(state);
  }
}

/** Removes the drained effects. The store calls this after performing them. */
export function clearEffects(state: ChatState): ChatState {
  return state.effects.length === 0 ? state : { ...state, effects: [] };
}
